import { useState } from 'react';
import { FaRegTrashCan } from 'react-icons/fa6';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { CustomButton } from '@/components/uiWrappers/button';
import { Badge } from '@/components/ui/badge';
import { CampusResourceData, College } from '@mightybyte/business';
import { CampusResourceFormModal } from './CampusResourceFormModal';
import { DeleteCampusResourceModal } from './DeleteCampusResourceModal';

interface CampusResourceCardProps {
  resource: CampusResourceData;
}

const UNIVERSITY_CONFIG: Record<College, {
  label: string;
  badgeClass: string;
}> = {
  [College.LMU]: {
    label: 'LMU',
    badgeClass: 'bg-brand',
  },
  [College.Lehigh]: {
    label: 'Lehigh University',
    badgeClass: 'bg-brand',
  },
};

export function CampusResourceCard({ resource }: CampusResourceCardProps) {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const config = UNIVERSITY_CONFIG[resource.university];

  return (
    <>
      <CampusResourceFormModal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        resource={resource}
      />
      <DeleteCampusResourceModal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        resourceId={resource._id}
        resourceTitle={resource.title}
      />

      <div className="rounded-[20px] overflow-hidden shadow-sm hover:shadow-md transition-shadow border border-gray-200 flex flex-col w-full bg-white">
        {/* Header section */}
        <div
          className="relative h-[100px] flex-shrink-0 flex flex-col justify-between p-4"
          style={{ backgroundColor: 'var(--quiz-bg-published)' }}
        >
          {/* University badge */}
          <Badge className={`absolute right-3 top-3 text-white ${config.badgeClass}`}>
            {config.label}
          </Badge>

          {/* Title */}
          <h3 className="font-bold text-base line-clamp-2 leading-snug mt-8 text-brand-dark">
            {resource.title}
          </h3>
        </div>

        {/* Link section */}
        <div className="px-3 pt-3 pb-1 flex-1 flex items-center gap-3 justify-between">
          <span className="text-xs bg-gray-100 rounded px-1.5 py-0.5 text-gray-700 font-medium">
            Order: {resource.order}
          </span>
          <a
            href={resource.link}
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand hover:text-brand/80 transition-colors flex items-center gap-1"
            title={resource.link}
          >
            <FaExternalLinkAlt className="w-3 h-3" />
            <span className="text-xs font-medium">Open Link</span>
          </a>
        </div>

        {/* Bottom actions */}
        <div className="bg-white px-3 py-3 flex items-center gap-3">
          <CustomButton
            variant="outline"
            className="flex-1"
            onClick={() => setIsEditOpen(true)}
          >
            Edit
          </CustomButton>

          <button
            onClick={() => setIsDeleteOpen(true)}
            className="w-[38px] h-[38px] text-red-500 bg-red-50 rounded-lg transition-colors justify-center items-center flex cursor-pointer hover:bg-red-100"
            title="Delete resource"
          >
            <FaRegTrashCan className="w-5 h-5" />
          </button>
        </div>
      </div>
    </>
  );
}
